/*
 *
 * File: CheckUserScreen.jsx
 *
 * Purpose: Check if user is registered in DrivePartner
 *          database and call booking component if user exist.
 *
 * Author:  Thomas Burgard (Software development)
 * Created: 10.03.2022
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

// Necessary for Stipe payment process
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { getUserData } from "../store/actions";
import BookingScreen from "./BookingScreen";
import FixPriceRoutesScreen from "./FixPriceRoutesScreen";
import ChangeBookingScreen from "./ChangeBookingScreen";
import CustomerInfoScreen from "./CustomerInfoScreen";
import DataProtectionScreen from "./DataProtectionScreen";
import LegalNoticeScreen from "./LegalNoticeScreen";
import TermsOfUseScreen from "./TermsOfUseScreen";
import PassengerLoginScreen from "./PassengerLoginScreen";
import PassengerRegisterScreen from "./PassengerRegisterScreen";
import PassengerStartScreen from "./passenger/PassengerStartScreen";
import PassengerAcceptBookingsScreen from "./passenger/PassengerAcceptBookingsScreen";
import PassengerAcceptBookingDetailsScreen from "./passenger/PassengerAcceptBookingDetailsScreen";
import BookingTestScreen from "./BookingTestScreen";
import SharedLayout from "./passenger/SharedLayout";
import { screenIdent } from "../utils/dataObjects";
import { getUserNameFromURL, getUserRegistrationData } from "../utils/utils";

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
let stripePromise;
/*const stripePromise = loadStripe(
  "pk_live_51Ok2KxI7lflkN3141Ho0uupslilmSi09DCjbON6NUwWWBOvBWvscgNB6sMkaeD9Fira98MPPRKPGSE2LHlQuzxKr00l38K7wae"
);*/

/**
 * "HOC-component" for checking if user is registered in DrivePartner database.
 * User comes from URL -> https://www.burgard.drivepartner.com -> burgard is
 * here the user name.
 *
 * If user exist, call multistop booking route calculation component, otherwise
 * show error information text.
 *
 * @returns JSX-HTML >DOM
 */
const CheckUserScreen = (props) => {
  const { t, i18n } = useTranslation("common");
  const screenID = props.screenIdent;

  //--------------------------------------------------------------
  //--- REDUX ----------------------------------------------------
  //--------------------------------------------------------------
  const userServerData = useSelector((state) => state.userReducer);
  const dispatch = useDispatch();

  /**
   * Get stripe publish key from given usename.
   *
   */
  const getStripePromise = () => {
    const userRegistrationDataObj = getUserRegistrationData(
      userServerData.userData
    );

    switch (userRegistrationDataObj.username) {
			case "nuernberger":
				stripePromise = loadStripe(
					"pk_live_51Ok2KxI7lflkN3141Ho0uupslilmSi09DCjbON6NUwWWBOvBWvscgNB6sMkaeD9Fira98MPPRKPGSE2LHlQuzxKr00l38K7wae"
				);
				break;
			case "primelane":
				stripePromise = loadStripe(
					"pk_live_51Oevv3DYclwD2cRipOhNkjzyCMUhSf7Ca1YCnOYtRkcZfEHGwhAGxtt0JNWrJy6ixMvWmnP92UWLSZDXHnNkwS6100GzLya1za"
				);
				break;
			case "flughafentransfer-dortmund":
				stripePromise = loadStripe(
					"pk_live_51RiwLqF5VhFKFeyTS5POXQfJceeIuBeUeBymDWXczm5qL6mUuegWyjikBX3NF2q1gXxDR0F0R6scmHe1nmfpxtIW00hXnqDYmB"
				);
				break;
			case "emil":
				stripePromise = loadStripe(
					"pk_live_51RjHXpHvnVPsSjx3PFAWQnW5iWwrfMjAZv10x3IONZJUqEyvxa8lZVOPOSHbGWfoCpfd8LZw8YPGWyOppchFYiDU003XNKfP63"
				);
				break;
			case "fayazi":
				stripePromise = loadStripe(
					"pk_live_51R4meCKwww5mYDOFtIgkoSLir5HT3CHvI5YtumrDYpwRvB6jZihALvOa0Axk1eopZGfQFAiyQCIc9VtVRVf5evuF00RZrF6Cfz"
				);
				break;
			default:
				stripePromise = loadStripe(
					"pk_live_51Ok2KxI7lflkN3141Ho0uupslilmSi09DCjbON6NUwWWBOvBWvscgNB6sMkaeD9Fira98MPPRKPGSE2LHlQuzxKr00l38K7wae"
				);
				break;
		}
  };

  /**
   * Check if user exists in server database after mounted component.
   * Make API access with url part "username".
   *
   * @returns boolean true if user exists otherwise false
   */
  useEffect(() => {
    // Get browser URL to fetch user name
    const url = window.location.href;

    // Fetch user name from browser url
    const user = getUserNameFromURL(url);

    // Call Redux action to fetch user data from server
    dispatch(getUserData(user));
  }, []);

  /**
   * Show screen dependent on screen ID.
   */
  const showScreen = () => {
    let screen = null;

    getStripePromise();

    switch (screenID) {
      case screenIdent.bookingScreen:
        screen = (
          <Elements stripe={stripePromise}>
            <BookingScreen userData={userServerData.userData} />
          </Elements>
        );
        break;
      case screenIdent.fixPriceRoutesScreen:
        screen = (
          <Elements stripe={stripePromise}>
            <FixPriceRoutesScreen userData={userServerData.userData} />
          </Elements>
        );
        break;
      case screenIdent.changeBookingScreen:
        screen = <ChangeBookingScreen userData={userServerData.userData} />;
        break;
      case screenIdent.customerInfo:
        screen = <CustomerInfoScreen userData={userServerData.userData} />;
        break;
      case screenIdent.dataProtection:
        screen = <DataProtectionScreen userData={userServerData.userData} />;
        break;
      case screenIdent.legalNotice:
        screen = <LegalNoticeScreen userData={userServerData.userData} />;
        break;
      case screenIdent.termsOfUse:
        screen = <TermsOfUseScreen userData={userServerData.userData} />;
        break;
      case screenIdent.sharedLayout:
        screen = <SharedLayout userData={userServerData.userData} />;
        break;
      case screenIdent.passengerLoginScreen:
        screen = <PassengerLoginScreen userData={userServerData.userData} />;
        break;
      case screenIdent.passengerRegisterScreen:
        screen = <PassengerRegisterScreen userData={userServerData.userData} />;
        break;
      case screenIdent.passengerStartScreen:
        screen = <PassengerStartScreen userData={userServerData.userData} />;
        break;
      case screenIdent.passengerAcceptBookingsScreen:
        screen = (
          <PassengerAcceptBookingsScreen userData={userServerData.userData} />
        );
        break;
      case screenIdent.passengerAcceptBookingsDetailsScreen:
        screen = (
          <PassengerAcceptBookingDetailsScreen
            userData={userServerData.userData}
          />
        );
        break;
      case screenIdent.bookingTestScreen:
        screen = <BookingTestScreen userData={userServerData.userData} />;
        break;
    }

    return screen;
  };

  //*************************************************************/
  //* Returns JSX code                                          */
  //*************************************************************/
  return (
    <>
      {userServerData.userData ? (
        userServerData.userData.userExist ? (
          !userServerData.userData.compDriver ? (
            showScreen()
          ) : (
            <>
              <div style={{ margin: "30px", color: "red" }}>
                <p>
                  {t("errortext.error-user-not-companydriver")}{" "}
                  <a
                    href={
                      "https://www." +
                      userServerData.userData.compDriver["COMPANYOWNERNAME"] +
                      ".drivepartner.com"
                    }
                  >
                    {"www." +
                      userServerData.userData.compDriver["COMPANYOWNERNAME"] +
                      ".drivepartner.com"}
                  </a>
                </p>
              </div>
              <div style={{ marginTop: "800px" }} />
            </>
          )
        ) : (
          <>
            <div style={{ margin: "30px", color: "red" }}>
              <p>
                {t("errortext.error-user-not-exist-1")}
                <br />
                <br />
                {t("errortext.error-user-not-exist-2")}
              </p>
            </div>
            <div style={{ marginTop: "800px" }} />
          </>
        )
      ) : null}
    </>
  );
};

export default CheckUserScreen;
